
import utils from '../../../utils'
import {
	GenogramConnectionCategoryEnum,
	getGenogramConnectionCategoryByEdgeData,
} from "@shared/Genogram/GenogramConnectionCategoryEnum";
import {
  MANUAL_CONNECTION_TARGET_ACTIVE_CLASS,
  MANUAL_CONNECTION_TARGET_CLASS
} from "@/core/recoveryCoachingApp/coach/clients/ClientDetails/Genogram/GenogramEditor/lib/GenogramManualConnectionTargetHintRenderer";

const MANUAL_FAMILY_DUPLICATE_LOG_PREFIX = "[edgehandles-manual-family-duplicate]"

function canStartOn (node) {
  const { options, previewEles, ghostEles, handleNode } = this
  const isPreview = el => previewEles.anySame(el)
  const isGhost = el => ghostEles.anySame(el)
  const userFilter = el => el.filter(options.handleNodes).length > 0
  const isHandle = el => handleNode.same(el)
  const isTemp = el => isPreview(el) || isHandle(el) || isGhost(el)

  const { enabled, active, grabbingNode } = this

  return (
    enabled && !active && !grabbingNode &&
    (node == null || (!isTemp(node) && userFilter(node)))
  )
}

function canStartDrawModeOn (node) {
  return this.canStartOn(node) && this.drawMode
}

function canStartNonDrawModeOn (node) {
  return this.canStartOn(node) && !this.drawMode
}

function isFamilyTargetEdge (edge) {
  let data = edge?.data?.() || {}
  let connectionCategory = getGenogramConnectionCategoryByEdgeData(data, null)
  return !data.lineSubType || connectionCategory === GenogramConnectionCategoryEnum.SOCIAL
}

function show (node) {
	if (node.data().isAuxNode) {
		return
	}
  let { options, drawMode } = this

  if (!this.canStartOn(node) || (drawMode && !options.handleInDrawMode)) { return }

  this.sourceNode = node

  this.setHandleFor(node)

  this.emit('show', this.hp(), this.sourceNode)

  return this
}

function hide () {
  this.removeHandle()

  this.emit('hide', this.hp(), this.sourceNode)

  return this
}

function start (node, options = {}) {
  if (!this.canStartOn(node)) { return }

  this.active = true

  this.sourceNode = node
  this.sourceNode.addClass('eh-source')

  if(!options.keepGesturesEnabled){
    this.disableGestures()
  }
  this.disableEdgeEvents()

  this.emit('start', this.hp(), node)
}

function startManual (node, position) {
  if (!this.canStartOn(node)) { return }

  this.manualMode = true
  this.lastCompleteTargetWasFamilyEdge = false
  this.lastManualWarningTargetId = null
  this.hide()
  this.showManualTargets(node)
  this.start(node, {keepGesturesEnabled: true})

  let startPosition = position || node.position()
  this.update(startPosition)

  return this
}

function updateManual (position) {
  if (!this.manualMode || !this.active) { return }

  return this.update(position)
}

function completeManual (target, event) {
  if (!this.manualMode || !this.active) { return }
  if (target.same(this.sourceNode)) { return }
  if (this.options.manualFamilyChecks) {
    this.logManualFamilyDuplicate("complete-target", {
      sourceNodeId: this.sourceNode.id(),
      targetId: target.id?.(),
      targetIsNode: target.isNode?.(),
      targetIsEdge: target.isEdge?.(),
      targetLineSubType: target.data?.("lineSubType"),
      targetParentEdgeId: target.data?.("parentEdgeId"),
    })
    if (this.shouldBlockManualFamilyEdgeConnection(target)) { return }
  }

  let didPreview = target.same(this.targetNode) || this.preview(target, false, event)
  if (!didPreview) { return }

  this.lastCompleteWasManual = true
  this.lastCompleteTargetWasFamilyEdge = this.isManualFamilyConnectionTarget(target)
  this.manualMode = false
  this.stop(event)

  return this
}

function isManualFamilyConnectionTarget (target) {
  let isFamilyEdge = target?.isEdge?.() && this.isFamilyTargetEdge(target)
  let isAuxNode = target?.isNode?.() && target.data("isAuxNode")
  return Boolean(isFamilyEdge || isAuxNode)
}

function shouldBlockManualFamilyEdgeConnection (target) {
  let shouldBlock = false
  let warningMessage = null
  if (this.isManualOwnAuxTarget(target)) {
    shouldBlock = true
    warningMessage = "This person is already part of this family."
  } else if (this.isManualOwnFamilyTarget(target)) {
    shouldBlock = true
    warningMessage = "Can't connect this person to their own family line."
  } else if (this.hasCommittedFamilyMembership(this.sourceNode, target)) {
    shouldBlock = true
    warningMessage = "This person is already part of this family."
  }
  this.logManualFamilyDuplicate("block-check", {
    sourceNodeId: this.sourceNode.id(),
    targetId: target?.id?.(),
    targetIsNode: target?.isNode?.(),
    targetIsEdge: target?.isEdge?.(),
    targetLineSubType: target?.data?.("lineSubType"),
    warningMessage,
    shouldBlock,
  })
  if (shouldBlock) {
    if (!this.shouldSuppressManualWarning(target)) {
      this.rememberManualWarningTarget(target)
      this.options.manualConnectionWarning({
        message: warningMessage,
        duration: 3500,
      })
    }
  }
  return shouldBlock
}

function isManualOwnAuxTarget (target) {
  if (!target?.isNode?.() || !target.data("isAuxNode")) {
    return false
  }
  return this.getAuxNodeChildNodeId(target) === this.sourceNode.id()
}

function isManualSourceFamilyEndpoint (targetEdge) {
  let sourceNode = this.sourceNode
  if (!targetEdge?.nonempty?.()) {
    return false
  }
  return targetEdge.source().same(sourceNode) || targetEdge.target().same(sourceNode)
}

function isManualOwnFamilyTarget (target) {
  let familyEdge = this.getManualTargetFamilyEdge(target)
  if (!familyEdge) {
    return false
  }
  return this.isManualSourceFamilyEndpoint(familyEdge)
}

function hasCommittedFamilyMembership (sourceNode, target) {
  let targetFamilyEdge = this.getManualTargetFamilyEdge(target)
  if (!targetFamilyEdge) {
    return false
  }
  return this.hasCommittedFamilyMembershipToFamilyEdge(sourceNode, targetFamilyEdge)
}

function hasCommittedFamilyMembershipToFamilyEdge (sourceNode, targetEdge) {
  let sourceNodeId = sourceNode.id()
  let targetEdgeId = targetEdge.id()
  let directEdgeMatch = this.cy.edges().some(edge => {
    if (edge.data("isPreview") === true) {
      return false
    }
    if (!this.isFamilyTargetEdge(edge)) {
      return false
    }
    if (edge.id() !== targetEdgeId) {
      return false
    }
    return edge.source().id() === sourceNodeId || edge.target().id() === sourceNodeId
  })
  let auxMatches = this.cy.nodes().filter(node => node.data("isAuxNode")).map(node => {
    let nodeData = node.data()
    let resolvedChildNodeId = this.getAuxNodeChildNodeId(node)
    let connectedEdgeIds = node.connectedEdges().map(edge => edge.id())
    let hasConnectedEdgeToSource = node.connectedEdges().some(edge => {
      if (edge.data("isPreview") === true) {
        return false
      }
      return edge.source().id() === sourceNodeId || edge.target().id() === sourceNodeId
    })
    return {
      id: node.id(),
      parentEdgeId: nodeData.parentEdgeId,
      childNodeId: nodeData.childNodeId,
      resolvedChildNodeId,
      connectedEdgeIds,
      hasConnectedEdgeToSource,
      isPreview: nodeData.isPreview === true,
      isSameFamilyLine: nodeData.parentEdgeId === targetEdgeId,
      isSamePerson: resolvedChildNodeId === sourceNodeId,
    }
  })
  let hasAuxMatch = auxMatches.some(match => {
    if (match.isPreview || !match.isSameFamilyLine) {
      return false
    }
    return match.isSamePerson || match.hasConnectedEdgeToSource
  })
  this.logManualFamilyDuplicate("aux-scan", {
    sourceNodeId,
    targetEdgeId,
    directEdgeMatch,
    edgeAuxNodes: targetEdge.data("auxNodes") || [],
    auxMatches,
    hasAuxMatch,
    hasMatch: directEdgeMatch || hasAuxMatch,
  })

  return directEdgeMatch || hasAuxMatch
}

function getManualTargetFamilyEdge (target) {
  if (target?.isEdge?.() && this.isFamilyTargetEdge(target)) {
    return target
  }
  if (!target?.isNode?.() || !target.data("isAuxNode")) {
    return false
  }
  let parentEdgeId = target?.data?.("parentEdgeId")
  if (!parentEdgeId) {
    return null
  }
  let parentEdge = this.cy.getElementById(parentEdgeId)
  if (!parentEdge?.nonempty?.()) {
    return null
  }
  return parentEdge
}

function getAuxNodeChildNodeId (auxNode) {
  let nodeData = auxNode.data()
  let childNodeId = nodeData.childNodeId
  if (childNodeId) {
    return childNodeId
  }

  let childNode = auxNode.neighborhood().nodes().filter(node => !node.data("isAuxNode"))[0] || null
  if (!childNode) {
    return null
  }

  return childNode.id()
}

function shouldSuppressManualWarning (target) {
  return this.lastManualWarningTargetId === this.getManualWarningTargetId(target)
}

function rememberManualWarningTarget (target) {
  this.lastManualWarningTargetId = this.getManualWarningTargetId(target)
}

function getManualWarningTargetId (target) {
  let targetType = target?.isEdge?.() ? "edge" : "node"
  return `${targetType}:${target?.id?.() || ""}`
}

function logManualFamilyDuplicate (stage, payload = {}) {
  console.log(`${MANUAL_FAMILY_DUPLICATE_LOG_PREFIX} ${stage}`, JSON.stringify(payload))
}

function showManualTargets (sourceNode) {
  let validTargets = this.cy.nodes().filter(node => {
    if (node.same(sourceNode)) { return false }
    if (typeof this.options.manualTargetNodes === 'function') {
      return this.options.manualTargetNodes(sourceNode, node)
    }
    return this.canStartOn(node)
  })
  let validAuxTargets = this.cy.nodes().filter(node => {
    if (!this.options.manualTargetAuxNodes) { return false }
    if (node.same(sourceNode) || !node.data("isAuxNode")) {
      return false
    }
    if (this.isManualOwnAuxTarget(node)) {
      return false
    }
    if (this.isManualOwnFamilyTarget(node)) {
      return false
    }
    return !this.hasCommittedFamilyMembership(sourceNode, node)
  })
  let validFamilyEdges = this.cy.edges().filter(edge => {
    if (typeof this.options.manualTargetEdges === 'function') {
      return this.options.manualTargetEdges(sourceNode, edge)
    }
    if (!this.isFamilyTargetEdge(edge)) {
      return false
    }
    if (this.isManualOwnFamilyTarget(edge)) {
      return false
    }
    return !this.hasCommittedFamilyMembership(sourceNode, edge)
  })
  validTargets.addClass(MANUAL_CONNECTION_TARGET_CLASS)
  validAuxTargets.addClass(MANUAL_CONNECTION_TARGET_CLASS)
  validFamilyEdges.addClass(MANUAL_CONNECTION_TARGET_CLASS)
}

function clearManualTargets () {
  this.cy.elements(`.${MANUAL_CONNECTION_TARGET_CLASS}, .${MANUAL_CONNECTION_TARGET_ACTIVE_CLASS}`)
    .removeClass(`${MANUAL_CONNECTION_TARGET_CLASS} ${MANUAL_CONNECTION_TARGET_ACTIVE_CLASS}`)
}

function syncManualActiveTarget (target) {
  this.cy.elements(`.${MANUAL_CONNECTION_TARGET_ACTIVE_CLASS}`)
    .removeClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
  if(this.manualMode && target?.hasClass?.(MANUAL_CONNECTION_TARGET_CLASS)){
    target.addClass(MANUAL_CONNECTION_TARGET_ACTIVE_CLASS)
  }
}

function cancelManual (event) {
  if (!this.manualMode) { return }

  this.lastCompleteWasManual = false
  this.lastCompleteTargetWasFamilyEdge = false
  this.lastManualWarningTargetId = null
  this.manualMode = false
  this.stop(event)

  return this
}

function update (pos) {
  if (!this.active) { return }

  let p = pos

  this.mx = p.x
  this.my = p.y
  this.lastManualWarningTargetId = null

  this.updateEdge()
  this.throttledSnap()

  return this
}

function snap () {
  if (!this.active || !this.options.snap) { return false }

  let cy = this.cy
  let tgt = this.targetNode
  let threshold = this.options.snapThreshold
  let sqThreshold = n => { let r = getRadius(n); let t = r + threshold; return t * t }
  let mousePos = this.mp()
  let sqDist = (p1, p2) => (p2.x - p1.x) * (p2.x - p1.x) + (p2.y - p1.y) * (p2.y - p1.y)
  let getRadius = n => (n.outerWidth() + n.outerHeight()) / 4
  let nodeSqDist = utils.memoize(n => sqDist(n.position(), mousePos), n => n.id())
  let isWithinTheshold = n => nodeSqDist(n) <= sqThreshold(n)
  let cmpSqDist = (n1, n2) => nodeSqDist(n1) - nodeSqDist(n2)
  let allowHoverDelay = false

  let nodesByDist = cy.nodes(isWithinTheshold).sort(cmpSqDist)
  let snapped = false

  if (tgt.nonempty() && !isWithinTheshold(tgt)) {
    this.unpreview(tgt)
  }

  for (let i = 0; i < nodesByDist.length; i++) {
    let n = nodesByDist[i]

    if (n.same(tgt) || this.preview(n, allowHoverDelay)) {
      snapped = true
      break
    }
  }

  return snapped
}

function preview (target, allowHoverDelay = true, event) {
  let { options, sourceNode, ghostNode, ghostEles, presumptiveTargets, previewEles, active } = this
  let source = sourceNode
  let isLoop = target.same(source)
  let loopAllowed = options.loopAllowed(target)
  let isGhost = target.same(ghostNode)
  let noEdge = !options.edgeType(source, target)
  let isHandle = target.same(this.handleNode)
  let isExistingTgt = target.same(this.targetNode)
  let isFilteredManualTarget = this.manualMode && !target?.hasClass?.(MANUAL_CONNECTION_TARGET_CLASS)

  if (!active || isHandle || isGhost || noEdge || isExistingTgt || isFilteredManualTarget || (isLoop && !loopAllowed)) { return false }

  if (this.targetNode.nonempty()) {
    this.unpreview(this.targetNode, event)
  }

  clearTimeout(this.previewTimeout)

  let applyPreview = () => {
    this.targetNode = target
    this.syncManualActiveTarget(target)

    presumptiveTargets.merge(target)

    target.addClass('eh-presumptive-target')
    target.addClass('eh-target')

    this.emit('hoverover', this.mp(), source, target)

    if (options.preview) {
      target.addClass('eh-preview')

      ghostEles.addClass('eh-preview-active')
      sourceNode.addClass('eh-preview-active')
      target.addClass('eh-preview-active')

      this.makePreview(event)

      this.emit('previewon', this.mp(), source, target, previewEles)
    }
  }

  if (allowHoverDelay && options.hoverDelay > 0) {
    this.previewTimeout = setTimeout(applyPreview, options.hoverDelay)
  } else {
    applyPreview()
  }

  return true
}

function unpreview (target, event) {
  if (!this.active || target.same(this.handleNode)) { return }

  let { previewTimeout, sourceNode, previewEles, ghostEles, cy } = this
  clearTimeout(previewTimeout)
  this.previewTimeout = null

  let source = sourceNode

  target.removeClass('eh-preview eh-target eh-presumptive-target eh-preview-active')
  ghostEles.removeClass('eh-preview-active')
  sourceNode.removeClass('eh-preview-active')

  this.targetNode = cy.collection()
  this.syncManualActiveTarget(null)

  this.removePreview(source, target)

  this.emit('hoverout', this.mp(), source, target)
  this.emit('previewoff', this.mp(), source, target, previewEles)

  return this
}

function stop (event) {
  if (!this.active) { return }
  this.manualMode = false
  this.clearManualTargets()

  let { sourceNode, targetNode, ghostEles, presumptiveTargets } = this

  clearTimeout(this.previewTimeout)

  sourceNode.removeClass('eh-source')
  targetNode.removeClass('eh-target eh-preview eh-hover')
  presumptiveTargets.removeClass('eh-presumptive-target')

  this.makeEdges(false, event)

  this.removeHandle()

  ghostEles.remove()
  this.connectivityNode = null

  this.clearCollections()

  this.resetGestures()
  this.enableEdgeEvents()

  this.active = false

  this.emit('stop', this.mp(), sourceNode)

  return this
}

export default {
  show,
  hide,
  start,
  startManual,
  showManualTargets,
  clearManualTargets,
  syncManualActiveTarget,
  updateManual,
  completeManual,
  isManualFamilyConnectionTarget,
  shouldBlockManualFamilyEdgeConnection,
  isManualOwnAuxTarget,
  isManualOwnFamilyTarget,
  isManualSourceFamilyEndpoint,
  hasCommittedFamilyMembership,
  hasCommittedFamilyMembershipToFamilyEdge,
  getManualTargetFamilyEdge,
  getAuxNodeChildNodeId,
  shouldSuppressManualWarning,
  rememberManualWarningTarget,
  getManualWarningTargetId,
  logManualFamilyDuplicate,
  cancelManual,
  update,
  preview,
  unpreview,
  stop,
  snap,
  isFamilyTargetEdge,
  canStartOn,
  canStartDrawModeOn,
  canStartNonDrawModeOn
}
